import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import createSlug from '@glass/common/modules/url/createSlug';
import Typography from '@glass/web/components/base/Typography';
import LinkRounded from '@glass/web/components/base/icons/LinkRounded';
import { CENTER } from '@glass/web/modules/theme/constants';
import makeStyles from '@glass/web/modules/theme/makeStyles';

const HEADER_HEIGHT = 64;

const useStyles = makeStyles()((theme) => {
  const ANCHOR_OFFSET = HEADER_HEIGHT + parseInt(theme.spacing(2), 10);
  return {
    root: {
      display: 'flex',
      alignItems: 'center',
      '& > a': {
        color: theme.palette.primary.main,
        alignItems: CENTER,
        visibility: 'hidden',
        display: 'flex',
        height: '100%',
        opacity: 0,
        transition: theme.transitions.create(['opacity', 'visibility']),
        paddingLeft: 6,
        paddingTop: ANCHOR_OFFSET,
        marginTop: -ANCHOR_OFFSET,
      },
      '&:hover > a': {
        opacity: 1,
        visibility: 'visible',
      },
    },
  };
});

function MarkdownHeading({ level, children }) {
  const { classes } = useStyles();
  const variant = useMemo(() => `h${Math.min(level + 2, 6)}`, [level]);
  const component = useMemo(() => `h${Math.min(level + 1, 6)}`, [level]);
  const slug = useMemo(() => {
    if (level < 4 && typeof children?.[0] === 'string') {
      return createSlug(children[0], { maxLength: 80 });
    }
    return null;
  }, [level, children]);
  const href = useMemo(() => `#${slug}`, [slug]);

  return (
    <Typography gutterBottom className={classes.root} component={component} variant={variant}>
      {children}
      {slug ? (
        // eslint-disable-next-line jsx-a11y/control-has-associated-label
        <a href={href} name={slug}>
          <LinkRounded />
        </a>
      ) : null}
    </Typography>
  );
}

MarkdownHeading.propTypes = {
  children: PropTypes.node,
  level: PropTypes.number.isRequired,
};

export default MarkdownHeading;
